import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { env } from '../../lib/env';

interface OAuthCallbackProps {
  provider: 'github' | 'jira';
}

export function OAuthCallback({ provider }: OAuthCallbackProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  );
  const [message, setMessage] = useState('Processing authentication...');
  const [hasExecuted, setHasExecuted] = useState(false);

  const handleCallback = useCallback(async () => {
    try {
      logger.debug('OAuth callback - Starting callback handling', { provider });
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');

      logger.debug('OAuth callback - URL params', {
        hasCode: !!code,
        hasState: !!state,
        hasError: !!error,
        error: error,
        provider,
      });

      if (error) {
        logger.error('OAuth callback - Error in URL params', { error, provider });
        throw new Error(`OAuth error: ${error}`);
      }

      if (!code) {
        logger.error('OAuth callback - No authorization code in URL', { provider });
        throw new Error('No authorization code received');
      }

      // Enhanced state validation with expiration
      const savedStateData = sessionStorage.getItem(`oauth_state_${provider}`);
      const userId = sessionStorage.getItem(`oauth_user_id_${provider}`);

      if (!savedStateData) {
        throw new Error('OAuth state not found - please try connecting again');
      }

      let savedState: string;
      let stateTimestamp: number;
      try {
        const parsedState = JSON.parse(savedStateData);
        savedState = parsedState.state;
        stateTimestamp = parsedState.timestamp;
      } catch {
        // Fallback for old format
        savedState = savedStateData;
        stateTimestamp = Date.now() - 10 * 60 * 1000; // Assume old if can't parse
      }

      // Check state expiration (5 minutes)
      const now = Date.now();
      if (now - stateTimestamp > 5 * 60 * 1000) {
        sessionStorage.removeItem(`oauth_state_${provider}`);
        sessionStorage.removeItem(`oauth_user_id_${provider}`);
        throw new Error('OAuth state expired - please try connecting again');
      }

      if (state !== savedState) {
        logger.error('OAuth callback - State mismatch', {
          received: state,
          expected: savedState,
          provider,
        });
        throw new Error(
          'State mismatch - possible CSRF attack or expired session'
        );
      }

      if (!userId && !user?.id) {
        throw new Error('User not authenticated');
      }

      const actualUserId = userId || user?.id;

      setMessage('Exchanging authorization code...');
      logger.debug('OAuth callback - Calling Edge function', {
        functionName: `${provider}-oauth-callback`,
        provider,
        userId: actualUserId,
      });

      // Get session for authentication header
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data: tokenData, error: tokenError } =
        await supabase.functions.invoke(`${provider}-oauth-callback`, {
          body: {
            code,
            user_id: actualUserId,
          },
          headers: {
            Authorization: session ? `Bearer ${session.access_token}` : '',
          },
        });

      logger.debug('OAuth callback - Edge function response', {
        hasData: !!tokenData,
        hasError: !!tokenError,
        error: tokenError,
        provider,
        responseStatus: tokenData?.status,
      });

      if (tokenError) {
        logger.error('Edge function error details', {
          error: tokenError.message,
          errorCode: tokenError.code,
          provider,
        });
        
        // Try to fetch detailed error payload directly for better diagnostics
        try {
          const res = await fetch(
            `${env.VITE_SUPABASE_URL}/functions/v1/${provider}-oauth-callback`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': session ? `Bearer ${session.access_token}` : '',
              },
              body: JSON.stringify({ code, user_id: actualUserId }),
            }
          );
          
          const responseText = await res.text();
          let errJson;
          try {
            errJson = JSON.parse(responseText);
          } catch {
            errJson = { message: responseText || `HTTP ${res.status}` };
          }
          
          const detail = errJson?.error || errJson?.message || errJson?.error_description || tokenError.message;
          
          // Provide helpful error message
          if (res.status === 404) {
            throw new Error(`OAuth Edge Function not found. Please ensure the '${provider}-oauth-callback' function is deployed in Supabase.`);
          } else if (res.status === 401 || res.status === 403) {
            throw new Error(`OAuth authentication failed. Please try connecting again.`);
          } else {
            throw new Error(`Failed to exchange code: ${detail}`);
          }
        } catch (fetchError) {
          if (fetchError instanceof Error && fetchError.message.includes('Edge Function not found')) {
            throw fetchError;
          }
          throw new Error(`Failed to exchange code: ${tokenError.message || 'Unknown error'}`);
        }
      }

      if (tokenData?.error) {
        logger.error('Edge function returned error', {
          error: tokenData.error,
        });
        throw new Error(`OAuth error: ${tokenData.error}`);
      }

      if (!tokenData || !tokenData.access_token) {
        logger.error('No access token in response', { tokenData });
        throw new Error('No access token received from OAuth provider');
      }

      setMessage('Saving connection...');

      const { error: dbError } = await supabase
        .from('user_connections')
        .upsert({
          user_id: actualUserId,
          provider,
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          token_expires_at: tokenData.expires_at,
          account_name: tokenData.account_name,
          account_email: tokenData.account_email,
          account_id: tokenData.account_id,
          scopes: tokenData.scopes,
          connected: true,
          status: 'active',
          last_connected: new Date().toISOString(),
        });

      if (dbError) {
        throw new Error(`Failed to save connection: ${dbError.message}`);
      }

      sessionStorage.setItem(`oauth_success_${provider}`, 'true');
      sessionStorage.removeItem(`oauth_state_${provider}`);
      sessionStorage.removeItem(`oauth_user_id_${provider}`);

      setStatus('success');
      setMessage(
        `${provider === 'github' ? 'GitHub' : 'Jira'} connected successfully!`
      );

      setTimeout(() => {
        window.close();
      }, 1500);
    } catch (error) {
      logger.error('OAuth callback error', { error, provider, userId: user?.id });
      setStatus('error');
      setMessage(
        error instanceof Error ? error.message : 'Authentication failed'
      );
      sessionStorage.setItem(`oauth_success_${provider}`, 'false');

      setTimeout(() => {
        window.close();
      }, 3000);
    }
  }, [provider, user?.id]);

  useEffect(() => {
    if (!hasExecuted) {
      setHasExecuted(true);
      handleCallback();
    }
  }, [handleCallback, hasExecuted]);

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
      <div className='max-w-md w-full p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg'>
        <div className='text-center'>
          {status === 'loading' && (
            <>
              <Loader2 className='w-16 h-16 text-accent-600 animate-spin mx-auto mb-4' />
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
                Connecting {provider === 'github' ? 'GitHub' : 'Jira'}
              </h2>
              <p className='text-gray-600 dark:text-gray-400'>{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className='w-16 h-16 text-green-600 mx-auto mb-4' />
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
                Success!
              </h2>
              <p className='text-gray-600 dark:text-gray-400'>{message}</p>
              <p className='text-sm text-gray-500 dark:text-gray-500 mt-4'>
                This window will close automatically...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className='w-16 h-16 text-red-600 mx-auto mb-4' />
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
                Connection Failed
              </h2>
              <p className='text-gray-600 dark:text-gray-400 mb-4'>{message}</p>
              <button
                onClick={() => window.close()}
                className='px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600'
              >
                Close Window
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
